import type { ReportType } from './types';

/**
 * Maps `ReportType` (named after the middle-end GraphQL types it represents) to the wire
 * vocabulary this module has always used — the abuse-reports REST API's `item_type`, the
 * Amplitude `*_report_abuse` event names, and the `fe.components.*_report_abuse` i18n
 * namespace (see `normalize.ts` and `useCategoryLabel.ts`). Renaming `ReportType`'s members
 * must never change any of that, so every one of those reads through this table instead of
 * `type` directly. A `Record` (via `satisfies`) rather than a switch, so adding a `ReportType`
 * member without a wire mapping fails to compile.
 *
 * Also reused for two things this table's shape happens to cover but isn't chosen for:
 * - The tracked Amplitude `role` property (`useReportAbuseTracking.ts`) — historically a
 *   separate hardcoded literal per report type, coincidentally identical to the `item_type`
 *   value. Reusing this table keeps that coincidence from silently drifting, but a future
 *   change to `item_type` made for REST/i18n reasons would also change the tracked `role`;
 *   if that's ever undesirable, split `role` into its own table instead of assuming they stay
 *   equal.
 * - The `data-qa` attribute on a report's trigger element. `ReportAbuseLink` (in the sibling
 *   `shared/components/abuse` unit) can't import this table directly — components folders may
 *   only export components/types — so `useOpenReportAbuseModal` computes `data-qa` from it and
 *   returns the string, which is how every trigger (via `ReportAbuseLink` or rendered directly,
 *   e.g. the video "more actions" menus) gets a `data-qa` that stays in sync with this table
 *   instead of a per-call-site literal.
 */
export const WIRE_REPORT_TYPE = {
	petition: 'petition',
	petitionSupporterComment: 'supporter_comment',
	petitionSupporterVideo: 'video_comment',
	generic: 'generic',
} as const satisfies Readonly<Record<ReportType, string>>;
